import { useState, useEffect, useRef } from 'react';
import { Smartphone, ShieldCheck, ShieldX, Clock, Loader2, Ban, Mail, UserCheck, ChevronLeft } from 'lucide-react';
import { audioAlerts } from '../../utils/audioAlerts';
import { getDeviceFingerprint } from '../../utils/deviceFingerprint';
import {
  createApprovalHandoff,
  getApprovalStatus,
  requestApprovalEmailCode,
  requestSuperiorApproval,
  respondTestApproval,
  verifyApprovalEmailCode,
} from '../../security/deviceApprovalApi';
import {
  ApprovalAccountSummary,
  ApprovalFallbackActions,
  ApprovalQrPanel,
  buildApprovePath,
  getSafeApprovalErrorMessage,
  getSafeQrCodeErrorMessage,
  secondsUntil,
  shouldHideQrByTimer,
  shouldShowTestAccess,
} from '../../security/approval';

const isMobileDevice = typeof window !== 'undefined' && (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth < 768);

type OptionalLocationResult = {
  lat?: number;
  lng?: number;
  geoStatus: 'ok' | 'unsupported' | 'denied' | 'unavailable';
  geoMessage?: string;
};

function geolocationErrorMessage(err: unknown) {
  const code = (err as GeolocationPositionError | undefined)?.code;
  if (code === 1) return 'GPS permission denied. Proceeding without location.';
  if (code === 3) return 'GPS timeout. Proceeding without location.';
  return 'GPS unavailable. Proceeding without location.';
}

async function getOptionalLocation(): Promise<OptionalLocationResult> {
  if (typeof navigator === 'undefined' || !navigator.geolocation) {
    return {
      geoStatus: 'unsupported',
      geoMessage: 'Geolocation is not supported by this browser.',
    };
  }

  try {
    const permission = await navigator.permissions?.query({ name: 'geolocation' as PermissionName });
    if (permission?.state === 'denied') {
      return {
        geoStatus: 'denied',
        geoMessage: 'Location permission is denied in the browser.',
      };
    }
  } catch {}

  const attempts: PositionOptions[] = [
    { timeout: 15000, maximumAge: 5 * 60 * 1000, enableHighAccuracy: false },
    { timeout: 12000, maximumAge: 10 * 60 * 1000, enableHighAccuracy: true },
  ];
  let lastError: unknown;

  for (const options of attempts) {
    try {
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, options);
      });
      return {
        lat: position.coords.latitude,
        lng: position.coords.longitude,
        geoStatus: 'ok',
      };
    } catch (err) {
      lastError = err;
      console.warn('Geolocation attempt failed:', err);
    }
  }

  return {
    geoStatus: 'unavailable',
    geoMessage: geolocationErrorMessage(lastError),
  };
}

interface Props {
  solicitacaoId: string;
  onApproved: (opcao?: string) => void;
  onDenied?: (msg?: string) => void;
  onBlocked?: (tipo: string) => void;
  onExpired?: () => void;
  onCancel?: () => void;
  timeoutSeconds?: number;
  perfil?: string;
  cargo?: string;
  accountName?: string;
  accountEmail?: string;
  dezenaCorreta?: string | null;
  error?: string;
}

type ApprovalStatus = 'PENDENTE' | 'AUTORIZADO' | 'NEGADO' | 'BLOQUEADO' | 'EXPIRADO' | 'CANCELADO';
type ViewState = 'wait_push' | 'options' | 'verify_email' | 'wait_superior';

export function DeviceApprovalWait({
  solicitacaoId, onApproved, onDenied, onBlocked, onExpired, onCancel,
  timeoutSeconds = 60, perfil, cargo, accountName, accountEmail, dezenaCorreta,
  error
}: Props) {
  const isTestAccessMode = shouldShowTestAccess(typeof window !== 'undefined' ? window.location.hostname : '');
  const accountDisplayName = accountName?.trim();
  const accountDisplayEmail = accountEmail?.trim();
  const [view, setView] = useState<ViewState>(isMobileDevice ? 'options' : 'wait_push');
  const [status, setStatus] = useState<ApprovalStatus>('PENDENTE');
  const [countdown, setCountdown] = useState(timeoutSeconds);
  const [tipoBloqueio, setTipoBloqueio] = useState<string | null>(null);

  // Estados para verificação de email
  const [emailOtp, setEmailOtp] = useState('');
  const [emailError, setEmailError] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);

  // Estados para aprovação do superior
  const [superiorSolicitacaoId, setSuperiorSolicitacaoId] = useState<string | null>(null);
  const [superiorLoading, setSuperiorLoading] = useState(false);
  const [superiorError, setSuperiorError] = useState('');
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [qrCodeLoading, setQrCodeLoading] = useState(false);
  const [qrCodeError, setQrCodeError] = useState('');
  const [qrCodeExpiresAt, setQrCodeExpiresAt] = useState('');

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const cdRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const doneRef = useRef(false);
  const onExpiredRef = useRef(onExpired);
  const qrRemainingSeconds = qrCodeExpiresAt ? Math.min(countdown, secondsUntil(qrCodeExpiresAt)) : 0;
  const hasUsableQrCode = status === 'PENDENTE' && countdown > 0 && qrRemainingSeconds > 0 && !!qrCodeUrl;
  const safeGeneralError = getSafeApprovalErrorMessage(error);

  useEffect(() => {
    onExpiredRef.current = onExpired;
  }, [onExpired]);

  const stopAllTimers = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    if (cdRef.current) {
      clearInterval(cdRef.current);
      cdRef.current = null;
    }
  };

  const finishLocalExpiration = () => {
    if (doneRef.current) return;
    doneRef.current = true;
    stopAllTimers();
    setStatus('EXPIRADO');
    setQrCodeUrl('');
    setQrCodeExpiresAt('');
    setQrCodeError('');
    playFeedbackSound('EXPIRADO');
    onExpiredRef.current?.();
  };

  const playFeedbackSound = (outcome: 'AUTORIZADO' | 'NEGADO' | 'BLOQUEADO' | 'EXPIRADO') => {
    if (!isMobileDevice) return;
    if (outcome === 'AUTORIZADO') {
      audioAlerts.playAcessoAutorizado();
      return;
    }
    try {
      const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
      if (outcome === 'NEGADO') {
        const playBuzz = (time: number, freq: number) => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.connect(gain); gain.connect(ctx.destination);
          osc.type = 'triangle';
          osc.frequency.setValueAtTime(freq, ctx.currentTime + time);
          gain.gain.setValueAtTime(0.25, ctx.currentTime + time);
          gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + time + 0.25);
          osc.start(ctx.currentTime + time);
          osc.stop(ctx.currentTime + time + 0.28);
        };
        playBuzz(0, 220); playBuzz(0.2, 180);
      } else if (outcome === 'BLOQUEADO') {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain); gain.connect(ctx.destination);
        osc.type = 'sawtooth';
        osc.frequency.setValueAtTime(300, ctx.currentTime);
        osc.frequency.linearRampToValueAtTime(80, ctx.currentTime + 0.6);
        gain.gain.setValueAtTime(0.3, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.7);
        osc.start(ctx.currentTime); osc.stop(ctx.currentTime + 0.8);
      } else if (outcome === 'EXPIRADO') {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain); gain.connect(ctx.destination);
        osc.type = 'sine';
        osc.frequency.setValueAtTime(440, ctx.currentTime);
        osc.frequency.linearRampToValueAtTime(220, ctx.currentTime + 0.3);
        gain.gain.setValueAtTime(0.25, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.35);
        osc.start(ctx.currentTime); osc.stop(ctx.currentTime + 0.4);
      }
      setTimeout(() => ctx.close(), 1500);
    } catch (e) {
      console.warn('[AudioAlerts] Falha ao reproduzir áudio:', e);
    }
  };

  // Polling e countdown para a solicitação inicial (celular)
  const startPollingPush = (id: string, initialTimeout: number) => {
    stopAllTimers();
    doneRef.current = false;
    setStatus('PENDENTE');
    setCountdown(initialTimeout);

    pollRef.current = setInterval(async () => {
      if (doneRef.current) return;
      try {
        const token = localStorage.getItem('ac_token') || '';
        const data = await getApprovalStatus(id, { bearerToken: token });
        if (data.status !== 'PENDENTE' && !doneRef.current) {
          doneRef.current = true;
          setStatus(data.status);
          stopAllTimers();
          if (data.status !== 'CANCELADO') {
            playFeedbackSound(data.status);
          }
          setTimeout(() => {
            if (data.status === 'AUTORIZADO') {
              onApproved((data.contexto as { opcao?: string } | null)?.opcao);
            } else if (data.status === 'NEGADO') {
              onDenied?.('Acesso recusado no celular.');
            } else if (data.status === 'BLOQUEADO') {
              setTipoBloqueio(data.tipoBloqueio ?? null);
              if (data.tipoBloqueio) onBlocked?.(data.tipoBloqueio);
            } else if (data.status === 'EXPIRADO') {
              setStatus('EXPIRADO');
            }
          }, 2000);
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : '';
        if (message.toLowerCase().includes('sess')) {
          doneRef.current = true;
          stopAllTimers();
          onDenied?.(message || 'Sessao encerrada. Outro login foi detectado.');
        }
      }
    }, 2000);

    cdRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          finishLocalExpiration();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };
  useEffect(() => {
    if (!isMobileDevice) {
      // audioAlerts.playSolicitandoAcesso();
      startPollingPush(solicitacaoId, timeoutSeconds);
    }
    return () => stopAllTimers();
  }, [solicitacaoId]);

  useEffect(() => {
    const cleanupSensitiveQr = () => {
      doneRef.current = true;
      stopAllTimers();
      setQrCodeUrl('');
      setQrCodeExpiresAt('');
      setQrCodeError('');
      setQrCodeLoading(false);
    };

    window.addEventListener('pagehide', cleanupSensitiveQr);
    window.addEventListener('beforeunload', cleanupSensitiveQr);
    return () => {
      window.removeEventListener('pagehide', cleanupSensitiveQr);
      window.removeEventListener('beforeunload', cleanupSensitiveQr);
      stopAllTimers();
    };
  }, []);

  useEffect(() => {
    if (isMobileDevice || !solicitacaoId) return;
    if (status !== 'PENDENTE') {
      setQrCodeUrl('');
      setQrCodeExpiresAt('');
      setQrCodeError('');
      setQrCodeLoading(false);
      return;
    }

    let cancelled = false;
    const emailForLogin = accountEmail?.trim();

    setQrCodeUrl('');
    setQrCodeExpiresAt('');
    setQrCodeError('');

    setQrCodeLoading(true);
    createApprovalHandoff(solicitacaoId)
      .then((data) => {
        if (cancelled) return;
        const approvalPath = buildApprovePath({
          solicitacaoId,
          email: emailForLogin,
          handoff: data.handoff,
        });
        setQrCodeUrl(`${window.location.origin}${approvalPath}`);
        setQrCodeExpiresAt(data.expiraEm);
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn('[DeviceApprovalWait] Falha ao gerar handoff do QR:', err);
        setQrCodeUrl('');
        setQrCodeExpiresAt('');
        setQrCodeError(getSafeQrCodeErrorMessage(err));
      })
      .finally(() => {
        if (!cancelled) setQrCodeLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [solicitacaoId, accountEmail, status]);

  useEffect(() => {
    if (shouldHideQrByTimer({
      status,
      approvalSecondsRemaining: countdown,
      handoffExpiresAt: qrCodeExpiresAt,
    })) {
      setQrCodeUrl('');
      setQrCodeExpiresAt('');
      setQrCodeError('');
      setQrCodeLoading(false);
    }
  }, [status, countdown, qrCodeExpiresAt]);

  // Função para enviar e-mail com o código OTP
  const handleRequestEmailCode = async () => {
    setEmailLoading(true);
    setEmailError('');
    const token = localStorage.getItem('ac_token') || '';
    try {
      await requestApprovalEmailCode({ bearerToken: token });
      setView('verify_email');
    } catch (err: any) {
      setEmailError(err.message || 'Falha ao solicitar codigo.');
    } finally {
      setEmailLoading(false);
    }
  };

  const handleVerifyEmailCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!emailOtp.trim() || emailOtp.length < 6) {
      setEmailError('Digite o codigo de 6 digitos.');
      return;
    }
    setEmailLoading(true);
    setEmailError('');
    const token = localStorage.getItem('ac_token') || '';
    try {
      await verifyApprovalEmailCode(
        {
          codigo: emailOtp.trim(),
          fingerprint: getDeviceFingerprint(),
        },
        { bearerToken: token },
      );
      playFeedbackSound('AUTORIZADO');
      onApproved('EMAIL_OTP');
    } catch (err: any) {
      setEmailError(err.message || 'Erro ao validar codigo.');
    } finally {
      setEmailLoading(false);
    }
  };

  const handleTestAccess = async () => {
    setSuperiorError('');
    setSuperiorLoading(true);
    try {
      await respondTestApproval(solicitacaoId, 'AUTORIZADO');
      playFeedbackSound('AUTORIZADO');
      onApproved('TESTE');
    } catch (err: any) {
      console.warn('[TestAccess] Falha ao registrar aprovacao no backend:', err);
      playFeedbackSound('AUTORIZADO');
      onApproved('TESTE');
    } finally {
      setSuperiorLoading(false);
    }
  };

  const renderTestAccessButton = () => {
    if (!isTestAccessMode) return null;

    return (
      <button
        onClick={handleTestAccess}
        disabled={superiorLoading}
        style={{
          ...testAccessButtonStyle,
          opacity: superiorLoading ? 0.7 : 1,
        }}
      >
        {superiorLoading ? <Loader2 size={16} className="animate-spin" /> : <UserCheck size={16} />}
        Simular liberação de acesso
      </button>
    );
  };

  const renderCancelButton = (alwaysShow = false) => {
    if (!alwaysShow && !onCancel) return null;

    return (
      <button
        onClick={onCancel}
        style={{
          ...btnStyle,
          marginTop: 8,
          width: '100%',
        }}
      >
        Voltar ao Login
      </button>
    );
  };

  const handleRequestSuperiorApproval = async () => {
    setSuperiorLoading(true);
    setSuperiorError('');
    const token = localStorage.getItem('ac_token') || '';

    try {
      const location = await getOptionalLocation().catch((err) => {
        console.warn('Geolocation failed outside standard flow:', err);
        const fallback: OptionalLocationResult = {
          geoStatus: 'unavailable',
          geoMessage: 'GPS unavailable. Proceeding without location.',
        };
        return fallback;
      });

      const data = await requestSuperiorApproval(
        {
          contexto: {
            fingerprint: getDeviceFingerprint(),
            ...(location.lat !== undefined ? { lat: location.lat } : {}),
            ...(location.lng !== undefined ? { lng: location.lng } : {}),
            geoStatus: location.geoStatus,
            geoMessage: location.geoMessage,
          },
        },
        { bearerToken: token },
      );
      setSuperiorSolicitacaoId(data.id);
      setView('wait_superior');
      startPollingPush(data.id, 600);
    } catch (err: any) {
      setSuperiorError(err.message || 'Falha ao solicitar aprovacao.');
    } finally {
      setSuperiorLoading(false);
    }
  };

  const progress = (countdown / (view === 'wait_superior' ? 600 : timeoutSeconds)) * 100;

  // Renderiza estados finais caso status mude em polling
  if (status === 'AUTORIZADO') {
    return (
      <div style={overlayStyle}>
        <div style={cardStyle}>
          <div style={{ ...iconWrap, background: 'rgba(108, 92, 231, 0.1)', borderColor: 'rgba(108, 92, 231, 0.25)' }}>
            <ShieldCheck size={36} strokeWidth={1.8} style={{ color: '#6c5ce7' }} />
          </div>
          <h2 style={{ ...titleStyle, color: '#6c5ce7' }}>Acesso Autorizado</h2>
          <p style={descStyle}>O acesso a este dispositivo foi autorizado.</p>
        </div>
      </div>
    );
  }

  if (status === 'NEGADO') {
    return (
      <div style={overlayStyle}>
        <div style={cardStyle}>
          <div style={{ ...iconWrap, background: '#fef2f2', borderColor: '#fecaca' }}>
            <ShieldX size={36} strokeWidth={1.8} style={{ color: '#ef4444' }} />
          </div>
          <h2 style={{ ...titleStyle, color: '#ef4444' }}>Acesso Rejeitado</h2>
          <p style={descStyle}>A solicitação de acesso foi rejeitada.</p>
          <button onClick={() => { setStatus('PENDENTE'); setView('options'); }} style={blueButtonStyle}>Tentar de Outra Forma</button>
        </div>
      </div>
    );
  }

  if (status === 'BLOQUEADO') {
    return (
      <div style={overlayStyle}>
        <div style={cardStyle}>
          <div style={{ ...iconWrap, background: '#fef2f2', borderColor: '#fecaca' }}>
            <Ban size={36} strokeWidth={1.8} style={{ color: '#dc2626' }} />
          </div>
          <h2 style={{ ...titleStyle, color: '#dc2626' }}>Dispositivo Bloqueado</h2>
          <p style={descStyle}>
            {tipoBloqueio === 'COMPLETO'
              ? 'Este dispositivo foi permanentemente bloqueado.'
              : 'Este dispositivo foi bloqueado para esta página.'}
          </p>
        </div>
      </div>
    );
  }

  // ── VIEW: OPTIONS (Tentar outra forma / Sem Celular) ──
  if (view === 'options') {
    const isMasterOrAdmin = perfil === 'ADMIN' || perfil === 'MASTER';
    const titleText = isMobileDevice ? 'Autentique para Autorizar' : 'Não foi possível autorizar pelo celular';
    const descText = isMobileDevice
      ? 'Este é um novo dispositivo móvel. Para autorizá-lo, escolha uma das alternativas abaixo:'
      : 'Escolha uma das alternativas abaixo para liberar seu acesso:';
    const superiorButtonLabel = isTestAccessMode ? 'Simular liberação de acesso' : 'Solicitar liberação ao superior';
    return (
      <div style={overlayStyle}>
        {isTestAccessMode && (
          <style>{`
          `}</style>
        )}
        <div style={cardStyle}>
          <div style={{ ...iconWrap, background: isMobileDevice ? 'rgba(108, 92, 231, 0.1)' : '#fffbeb', borderColor: isMobileDevice ? 'rgba(108, 92, 231, 0.25)' : '#fde68a' }}>
            {isMobileDevice ? (
              <ShieldCheck size={36} strokeWidth={1.8} style={{ color: '#6c5ce7' }} />
            ) : (
              <Clock size={36} strokeWidth={1.8} style={{ color: '#f59e0b' }} />
            )}
          </div>
          <h2 style={isMobileDevice ? { ...titleStyle, color: '#6c5ce7' } : titleStyle}>{titleText}</h2>
          <p style={{ ...descStyle, marginBottom: 16 }}>{descText}</p>

          <ApprovalAccountSummary name={accountDisplayName} email={accountDisplayEmail} />

          <ApprovalFallbackActions
            emailLoading={emailLoading}
            superiorLoading={superiorLoading}
            isTestAccessMode={false}
            emailError={emailError}
            superiorError={superiorError}
            generalError={safeGeneralError}
            superiorButtonLabel={isTestAccessMode ? 'Solicitar liberação ao superior' : superiorButtonLabel}
            onRequestEmailCode={handleRequestEmailCode}
            onRequestSuperiorApproval={handleRequestSuperiorApproval}
            onTestAccess={handleTestAccess}
          />
          {renderTestAccessButton()}
          {renderCancelButton(true)}
        </div>
      </div>
    );
  }

  // ── VIEW: VERIFY EMAIL (Digitar código OTP) ──
  if (view === 'verify_email') {
    return (
      <div style={overlayStyle}>
        <div style={cardStyle}>
          <button
            onClick={() => { setView('options'); setEmailError(''); }}
            style={{
              position: 'absolute',
              top: 16,
              left: 16,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: '#64748b',
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              fontSize: '0.75rem',
              fontWeight: 600,
            }}
          >
            <ChevronLeft size={16} /> Voltar
          </button>

          <div style={{ ...iconWrap, background: 'rgba(108, 92, 231, 0.1)', borderColor: 'rgba(108, 92, 231, 0.25)', marginTop: 12 }}>
            <Mail size={32} strokeWidth={1.8} style={{ color: '#6c5ce7' }} />
          </div>

          <h2 style={titleStyle}>Confirmar Código</h2>
          <p style={descStyle}>
            Enviamos um código de 6 dígitos para o seu e-mail.<br />
            Digite-o abaixo para confirmar seu acesso.
          </p>

          <form onSubmit={handleVerifyEmailCode} style={{ width: '100%', marginTop: 16 }}>
            <div className="form-group" style={{ textAlign: 'left' }}>
              <label className="form-label" style={{ textAlign: 'center', display: 'block' }}>Código de 6 dígitos</label>
              <input
                type="text"
                maxLength={6}
                value={emailOtp}
                onChange={e => setEmailOtp(e.target.value.replace(/\D/g, ''))}
                placeholder="000000"
                className="form-input"
                style={{
                  textAlign: 'center',
                  fontSize: '1.25rem',
                  letterSpacing: '6px',
                  fontWeight: 700,
                }}
                disabled={emailLoading}
              />
              {emailError && <span className="form-error-text" style={{ textAlign: 'center', display: 'block' }}>{emailError}</span>}
              {error && <span className="form-error-text" style={{ textAlign: 'center', display: 'block' }}>{error}</span>}
            </div>

            <button
              type="submit"
              disabled={emailLoading || emailOtp.length < 6}
              className="btn-save"
              style={{
                width: '100%',
                marginTop: 12,
                padding: '12px',
                borderRadius: 12,
                fontSize: '0.82rem',
              }}
            >
              {emailLoading ? <Loader2 size={16} className="animate-spin" style={{ margin: '0 auto' }} /> : 'Confirmar e Acessar'}
            </button>
          </form>

          <button
            onClick={handleRequestEmailCode}
            disabled={emailLoading}
            style={{
              background: 'none',
              border: 'none',
              color: '#6c5ce7',
              fontSize: '0.75rem',
              fontWeight: 700,
              cursor: 'pointer',
              marginTop: 16,
              textDecoration: 'underline',
            }}
          >
            Reenviar código por e-mail
          </button>
        </div>
      </div>
    );
  }

  // ── VIEW: WAIT SUPERIOR (Aguardando liberação do Diretor) ──
  if (view === 'wait_superior') {
    return (
      <div style={overlayStyle}>
        <div style={cardStyle}>
          <div style={{ position: 'relative', marginBottom: 8 }}>
            <div style={{
              ...iconWrap,
              background: 'rgba(108, 92, 231, 0.1)', borderColor: 'rgba(108, 92, 231, 0.25)',
              animation: 'daw-pulse 2s ease-in-out infinite',
              marginBottom: 0,
            }}>
              <UserCheck size={32} strokeWidth={1.8} style={{ color: '#6c5ce7' }} />
            </div>
            <div style={{
              position: 'absolute', bottom: -2, right: -2,
              background: '#fff', borderRadius: '50%', padding: 3,
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            }}>
              <Loader2 size={16} style={{ color: '#6c5ce7', animation: 'daw-spin 1s linear infinite' }} />
            </div>
          </div>

          <h2 style={titleStyle}>Aguardando Liberação</h2>
          <p style={descStyle}>
            Solicitação enviada aos superiores da escola.<br />
            Por favor, aguarde a aprovação nesta tela.
          </p>

          <div style={{ width: '100%', marginTop: 16 }}>
            <div style={{
              height: 4, borderRadius: 4, background: '#e2e8f0',
              overflow: 'hidden',
            }}>
              <div style={{
                height: '100%', borderRadius: 4,
                background: countdown <= 60
                  ? 'linear-gradient(90deg, #ef4444, #dc2626)'
                  : 'linear-gradient(90deg, #6c5ce7, #8e2de2)',
                width: `${progress}%`,
                transition: 'width 1s linear',
              }} />
            </div>
            <div style={{
              marginTop: 8, fontSize: '0.75rem', color: '#94a3b8',
              display: 'flex', justifyContent: 'space-between',
            }}>
              <span>Expira em</span>
              <span style={{
                color: countdown <= 60 ? '#ef4444' : '#6c5ce7',
                fontWeight: 600,
              }}>
                {Math.floor(countdown / 60)}m {countdown % 60}s
              </span>
            </div>
          </div>

          <ApprovalAccountSummary name={accountDisplayName} email={accountDisplayEmail} />

          <button
            onClick={() => {
              stopAllTimers();
              setView('options');
            }}
            style={{
              ...blueButtonStyle,
              marginTop: 16,
              width: '100%',
            }}
          >
            Tentar outra forma
          </button>
        </div>
      </div>
    );
  }

  // ── VIEW: WAIT PUSH (Aguardando push inicial no celular — Unificado / Duas Colunas) ──
  const isExpired = status === 'EXPIRADO';
  const useDesktopLayout = !isMobileDevice;
  const shouldHideCurrentQr = shouldHideQrByTimer({
    status,
    approvalSecondsRemaining: countdown,
    handoffExpiresAt: qrCodeExpiresAt,
  });
  const showQrColumn = useDesktopLayout && !isExpired && !shouldHideCurrentQr && (qrCodeLoading || hasUsableQrCode);
  const useTwoColumnLayout = useDesktopLayout && showQrColumn;

  return (
    <div style={overlayStyle}>
      <div style={useTwoColumnLayout ? desktopCardStyle : cardStyle}>

        {/* Coluna 1: Painel de Autorização */}
        <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          paddingRight: useTwoColumnLayout ? 32 : 0,
          boxSizing: 'border-box',
        }}>
          <div style={{ position: 'relative', marginBottom: 8 }}>
            <div style={{
              ...iconWrap,
              background: isExpired ? '#fffbeb' : 'rgba(108, 92, 231, 0.1)',
              borderColor: isExpired ? '#fde68a' : 'rgba(108, 92, 231, 0.25)',
              animation: isExpired ? undefined : 'daw-pulse 2s ease-in-out infinite',
              marginBottom: 0,
            }}>
              {isExpired ? (
                <Clock size={36} strokeWidth={1.8} style={{ color: '#f59e0b' }} />
              ) : (
                <Smartphone size={32} strokeWidth={1.8} style={{ color: '#6c5ce7' }} />
              )}
            </div>
            {!isExpired && (
              <div style={{
                position: 'absolute', bottom: -2, right: -2,
                background: '#fff', borderRadius: '50%', padding: 3,
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              }}>
                <Loader2 size={16} style={{ color: '#6c5ce7', animation: 'daw-spin 1s linear infinite' }} />
              </div>
            )}
          </div>

          <h2 style={titleStyle}>
            {isExpired
              ? 'Não foi possível autorizar pelo celular'
              : (dezenaCorreta ? 'Autorização por Voz / Dezenas' : 'Aguardando Autorização')
            }
          </h2>
          {isExpired ? (
            <p style={descStyle}>
              Escolha uma das alternativas abaixo para liberar seu acesso:
            </p>
          ) : dezenaCorreta ? (
            <>
              <p style={descStyle}>
                Abra a notificação no celular e diga (ou selecione) a dezena abaixo:
              </p>
              <div style={{
                fontSize: '4.5rem',
                fontWeight: 900,
                color: '#6c5ce7',
                margin: '16px 0',
                letterSpacing: '2px',
                fontFamily: 'Outfit, Inter, sans-serif',
                animation: 'daw-pulse 2s ease-in-out infinite'
              }}>
                {dezenaCorreta}
              </div>
            </>
          ) : (
            <p style={descStyle}>
              Enviamos uma notificação para o seu celular.<br />
              Toque em <strong style={{ color: '#6c5ce7' }}>Autorizar</strong> para liberar o acesso.
            </p>
          )}

          {!isExpired && (
            <div style={{ width: '100%', marginTop: 16 }}>
              <div style={{
                height: 4, borderRadius: 4, background: '#e2e8f0',
                overflow: 'hidden',
              }}>
                <div style={{
                  height: '100%', borderRadius: 4,
                  background: countdown <= 10
                    ? 'linear-gradient(90deg, #ef4444, #dc2626)'
                    : 'linear-gradient(90deg, #6c5ce7, #8e2de2)',
                  width: `${progress}%`,
                  transition: 'width 1s linear',
                }} />
              </div>
              <div style={{
                marginTop: 8, fontSize: '0.75rem', color: '#94a3b8',
                display: 'flex', justifyContent: 'space-between',
              }}>
                <span>Expira em</span>
                <span style={{
                  color: countdown <= 10 ? '#ef4444' : '#6c5ce7',
                  fontWeight: 600,
                }}>
                  {countdown}s
                </span>
              </div>
            </div>
          )}

          <ApprovalAccountSummary name={accountDisplayName} email={accountDisplayEmail} />

          <ApprovalFallbackActions
            emailLoading={emailLoading}
            superiorLoading={superiorLoading}
            isTestAccessMode={false}
            emailError={emailError}
            superiorError={superiorError}
            generalError={safeGeneralError}
            onRequestEmailCode={handleRequestEmailCode}
            onRequestSuperiorApproval={handleRequestSuperiorApproval}
            onTestAccess={handleTestAccess}
          />
          {renderTestAccessButton()}
          {renderCancelButton()}
        </div>

        {/* Coluna 2: QR Code de Aprovação / Instalação (Somente Desktop) */}
        {showQrColumn && (
          <ApprovalQrPanel
            loading={qrCodeLoading}
            qrCodeUrl={qrCodeUrl}
            error={qrCodeError}
            remainingSeconds={qrRemainingSeconds}
            hasUsableQrCode={hasUsableQrCode}
            title="Autorizar por QR Code"
          />
        )}

        <style>{`
          @keyframes daw-pulse {
            0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(108, 92, 231, 0.2); }
            50% { transform: scale(1.05); box-shadow: 0 0 20px 8px rgba(108, 92, 231, 0.1); }
          }
          @keyframes daw-spin { to { transform: rotate(360deg); } }
        `}</style>
      </div>
    </div>
  );
}

// ── Styles ──────────────────────────────────────
const overlayStyle: React.CSSProperties = {
  position: 'fixed', inset: 0,
  background: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(12px)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  zIndex: 99998, padding: 20,
};

const cardStyle: React.CSSProperties = {
  background: '#ffffff', borderRadius: 24, padding: 'clamp(28px, 5vw, 40px)',
  maxWidth: 420, width: '100%', position: 'relative',
  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
  boxShadow: '0 25px 60px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.04)',
  textAlign: 'center',
};

const desktopCardStyle: React.CSSProperties = {
  ...cardStyle,
  maxWidth: 780,
  flexDirection: 'row',
  alignItems: 'stretch',
  gap: 0,
};

const iconWrap: React.CSSProperties = {
  width: 72, height: 72, borderRadius: 22,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  border: '2px solid', marginBottom: 8,
};

const titleStyle: React.CSSProperties = {
  fontSize: '1.15rem', fontWeight: 800, color: '#1e293b',
  margin: '4px 0 0', letterSpacing: '-0.01em',
};

const descStyle: React.CSSProperties = {
  fontSize: '0.82rem', color: '#64748b', lineHeight: 1.6,
};

const btnStyle: React.CSSProperties = {
  padding: '10px 28px', borderRadius: 12,
  border: '1.5px solid #e2e8f0', background: '#f8fafc',
  color: '#64748b', cursor: 'pointer',
  fontSize: '0.82rem', fontWeight: 600, marginTop: 12,
  transition: 'all 0.2s',
};

const blueButtonStyle: React.CSSProperties = {
  ...btnStyle,
  border: 'none',
  background: '#2563eb',
  color: '#ffffff',
  fontWeight: 800,
  boxShadow: '0 10px 24px rgba(37, 99, 235, 0.24)',
};

const testAccessButtonStyle: React.CSSProperties = {
  ...blueButtonStyle,
  width: '100%',
  marginTop: 10,
  background: '#e5e7eb',
  color: '#111827',
  border: '1px solid #cbd5e1',
  boxShadow: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
};
